import React, { useState } from 'react';
import { createdByName } from '../../../logic/task';
import { ELATION_PATIENT_BASE_URL } from '../../../config/medical-constants';
import {
  ROLE_LABELS,
  templateCodeOf,
  REPORTING_KIND_BY_TEMPLATE,
  ELATION_DEEPLINK_TEMPLATES,
} from './constants';
import {
  taskSnooze, isSnoozable, isSystemCompletedTask, appointmentPresentation, isTaskOverdue, clinicCallOf, isClinicCallTask, taskDisplayTitle,
  doneStampOf, completionLineOf, callCancelCopy,
} from '@/logic/task';
import ClaimEvidenceStatus from '../../ClaimEvidenceStatus';
import CareUpdateStatus from '../../CareUpdateStatus';
import LabResultsStatus from '../../LabResultsStatus';
import SpruceInboundStatus from '../../SpruceInboundStatus';
import TaskIntakeFlags from '../../TaskIntakeFlags';
import WithdrawalRequestStatus from '../../WithdrawalRequestStatus';
import { isWithdrawalRequest } from '../../../logic/withdrawalRequest';

/**
 * Task row. Every callback is optional and only truthiness-checked — the card renders the
 * affordance for whichever ones it is given.
 *
 * The JSDoc is load-bearing: this is a .jsx file, so TS infers a prop's type from its DEFAULT.
 * `onCloseTask = null` therefore types the prop as literally `null`, and any .tsx caller passing
 * a function fails tsc. Annotating the props keeps them optional AND callable.
 *
 * @param {{
 *   task: any,
 *   onComplete?: Function, onReopen?: Function, onCloseTask?: Function,
 *   onOrderLab?: Function, onAlignCms?: Function, onRunCmsEligibility?: Function,
 *   onRunCmsReporting?: Function, onOpenCarePlan?: Function,
 *   onSnooze?: Function, onResume?: Function, onNotThisPatient?: Function,
 *   viewerRole?: string, ehrPatientId?: any, suppressRole?: any,
 * }} props
 */
export default function TaskCard({ task, onComplete, onReopen, onCloseTask = null, onOrderLab, onAlignCms, onRunCmsEligibility, onRunCmsReporting, onOpenCarePlan, onSnooze = null, onResume = null, onNotThisPatient = null, viewerRole = 'care_coordinator', ehrPatientId = null, suppressRole = null }) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const slideRef = React.useRef(null);
  const kebabRef = React.useRef(null);
  React.useEffect(() => {
    if (!actionsOpen) return undefined;
    const onDown = (e) => { if (!slideRef.current?.contains(e.target)) setActionsOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setActionsOpen(false); kebabRef.current?.focus(); } };
    // mousedown (not click) so the drawer closes before the next row's kebab fires.
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [actionsOpen]);

  // Hide the owner-role pill when it just echoes the section it sits in
  // (e.g. a "Clinician" pill inside the Clinician-tasks list is redundant).
  const ownerLabel = task.owner_role ? (ROLE_LABELS[task.owner_role] || task.owner_role) : null;
  const suppressLabel = suppressRole ? (ROLE_LABELS[suppressRole] || suppressRole) : null;
  const showRoleTag = !!ownerLabel && ownerLabel !== suppressLabel;
  const canOrderLab = !!onOrderLab
    && task.clinical_domain === 'lab'
    && (task.task_type === 'action' || /_labs_order$/.test(templateCodeOf(task)))
    // Deep-link templates have an auto-placed draft order — e-sign via the link, not a WF31 re-order.
    && !ELATION_DEEPLINK_TEMPLATES.has(templateCodeOf(task))
    && !['completed', 'cancelled', 'missed'].includes(task.status);
  const isAlignmentTask = templateCodeOf(task) === 'onb_cms_alignment'
    && !['completed', 'cancelled', 'missed'].includes(task.status);
  // Eligibility button stays visible even on completed tasks — operator may
  // want to re-verify after insurance changes.
  const isEligibilityTask = templateCodeOf(task) === 'onb_eligibility_check';
  // rpt_*_submission tasks get the "Run CMS Reporting" button. Hidden once the
  // task is completed (CMS submission for the period is final).
  const reportingKind = REPORTING_KIND_BY_TEMPLATE[templateCodeOf(task)] || null;
  const isReportingTask = reportingKind !== null
    && !['completed', 'cancelled', 'missed'].includes(task.status);
  // Tasks the coordinator completes inside Elation rather than the back-office —
  // surface a deep-link to the patient's chart on the active card.
  const isElationLinkTask = ELATION_DEEPLINK_TEMPLATES.has(templateCodeOf(task))
    && !!ehrPatientId
    && !['completed', 'cancelled', 'missed'].includes(task.status);
  const elationLinkTitle = {
    onb_care_plan_share: "Open this patient's Elation chart to share the care plan with their PCP",
    cpc_care_plan_share_pcp: "Open this patient's Elation chart to share the updated care plan with their PCP",
    onb_baseline_labs_order: "Open this patient's Elation chart to review and e-sign the baseline lab order",
  }[templateCodeOf(task)]
    || "Open this patient's Elation chart to submit the monthly G-code claim";
  // Care-plan shortcut — onb_care_plan_create / md_review switch to the Care Plan
  // tab. Clinician-owned templates gate the label on viewerRole so coordinators
  // get a read-only verb.
  const carePlanCode = templateCodeOf(task);
  const isCarePlanTask = !!onOpenCarePlan
    && (carePlanCode === 'onb_care_plan_create'
        || carePlanCode === 'onb_care_plan_md_review')
    && !['completed', 'cancelled', 'missed'].includes(task.status);
  const viewerIsClinician = viewerRole === 'clinician';
  const carePlanLabel = carePlanCode === 'onb_care_plan_create'
    ? (viewerIsClinician ? 'Create care plan' : 'View care plan')
    : (viewerIsClinician ? 'Review care plan' : 'View care plan');
  const [completing, setCompleting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isDone = ['completed', 'cancelled', 'missed'].includes(task.status);
  // WF69 stamps context_json.appointment from Elation. When it does, the appointment IS the
  // explanation for this date, so the card must not also assert lateness — a booked slot in the
  // past is unresolved, not late.
  const appt = appointmentPresentation(task);
  const isOverdue = isTaskOverdue(task);
  const dueLabel = task.due_at
    ? new Date(task.due_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  // A clinic-line call is drawn from its own guarded block: its label, its time, never "Auto-completed".
  const call = clinicCallOf(task);
  const doneStamp = doneStampOf(task);
  const doneLabel = doneStamp
    ? new Date(doneStamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  const DONE_STYLES = {
    completed: { label: 'Completed', color: 'var(--wi-text-primary-good)' },
    cancelled: { label: 'Cancelled', color: 'var(--wi-text-secondary)' },
    missed:    { label: 'Missed',    color: 'var(--wi-status-bad)' },
  };
  const doneCfg = isDone ? DONE_STYLES[task.status] : null;
  const completionLine = isDone ? completionLineOf(task) : null;
  const completionByLine = completionLine?.by ?? null;
  // Ad-hoc staff tasks carry context_json.created_by — surface "Created by <name>"
  // on the open card (completed cards already show the completer).
  const creatorName = !isDone ? createdByName(task) : null;

  const PRIO_COLORS = {
    urgent: { border: 'var(--wi-status-bad)', bg: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)' },
    high:   { border: '#F59E0B', bg: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)' },
    normal: { border: 'var(--wi-status-undefined)', bg: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)' },
    low:    { border: 'var(--wi-status-good)', bg: 'var(--wi-background-good)', color: 'var(--wi-text-primary-good)' },
  };
  const prio = PRIO_COLORS[task.priority] || PRIO_COLORS.normal;

  async function handleClick() {
    if (!onComplete || completing) return;
    setCompleting(true);
    // Falsy = the parent deferred to a confirm gate instead of completing;
    // release the checkbox so a dismissed modal doesn't wedge it disabled.
    const completed = await onComplete(task.task_id);
    if (!completed) setCompleting(false);
  }

  const canNotThisPatient = !!onNotThisPatient && !!call && task.status === 'completed' && !call.voided;

  async function handleReopen() {
    if (!onReopen || reopening) return;
    setReopening(true);
    await onReopen(task.task_id);
  }

  const snz = taskSnooze(task);


  const hasActions = canOrderLab
    || (isAlignmentTask && onAlignCms)
    || (isEligibilityTask && onRunCmsEligibility)
    || (isReportingTask && onRunCmsReporting)
    || isCarePlanTask
    || isElationLinkTask;

  // WF17 refuses every change to a withdrawal request (only the Withdraw window cancels it, through WF67) and to a
  // clinic-line call but its "Not this patient" cancel, whatever state another door left it in.
  const locked = isWithdrawalRequest(task);
  const frozen = locked || isClinicCallTask(task);
  const canSnooze = !isDone && !frozen && onSnooze && isSnoozable(task);
  const canResume = !isDone && !frozen && onResume && snz;
  const canClose = !isDone && !frozen && !!onCloseTask;

  return (
    <div className={`detail-task-card task-slide${actionsOpen ? ' is-open' : ''}`} ref={slideRef} style={{ opacity: completing ? 0.5 : 1, transition: 'opacity 300ms ease' }}>
      <div className="task-slide__body">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Clickable checkbox circle */}
        {onComplete && !frozen && !isSystemCompletedTask(task.template_code) ? (
          <button
            onClick={handleClick}
            disabled={completing}
            title="Mark as complete"
            className="task-check-btn"
            style={{ '--check-color': prio.border }}
          >
            {completing ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            ) : null}
          </button>
        ) : (
          <div style={{
            marginTop: 4, width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            background: prio.border,
          }} />
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span className="body2" style={{ fontWeight: 500 }}>{taskDisplayTitle(task)}</span>
            {(task.priority === 'urgent' || task.priority === 'high') && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: prio.bg, color: prio.color, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                {task.priority}
              </span>
            )}
            {showRoleTag && (
              <span
                title={`Assigned to ${ownerLabel}`}
                style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                {ownerLabel}
              </span>
            )}
            {/* Snoozed rows announce themselves next to the other pills — visible at a glance,
                no need to open the drawer. Loud on purpose: it explains why an overdue row is
                not being chased. */}
            {!isDone && snz && (
              <span
                className="task-pill-snoozed"
                title={snz.reason ? `Snoozed: ${snz.reason}` : 'Snoozed'}
              >
                {`💤 Snoozed until ${snz.until.slice(5)}`}
              </span>
            )}
          </div>
          {task.description && (
            (() => {
              const isLong = task.description.length > 120;
              return (
                <p
                  className="detail1 text-secondary"
                  onClick={isLong ? (e) => { e.stopPropagation(); setExpanded(v => !v); } : undefined}
                  style={{
                    margin: '3px 0 0',
                    lineHeight: 1.4,
                    cursor: isLong ? 'pointer' : 'default',
                  }}
                  title={isLong ? (expanded ? 'Click to collapse' : 'Click to expand') : undefined}
                >
                  {isLong && !expanded ? task.description.slice(0, 120) + '…' : task.description}
                  {isLong && (
                    <span style={{ marginLeft: 6, color: 'var(--wi-action-primary)', fontWeight: 500 }}>
                      {expanded ? 'Show less' : 'Show more'}
                    </span>
                  )}
                </p>
              );
            })()
          )}
          {creatorName && (
            <p className="detail1 text-secondary" style={{ margin: '3px 0 0', fontSize: 11 }}>
              Created by {creatorName}
            </p>
          )}
          <TaskIntakeFlags task={task} />
          <WithdrawalRequestStatus task={task} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          {isDone && doneCfg ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <span className="detail1" style={{ color: doneCfg.color, fontWeight: 500 }}>
                {(completionLine?.verb || doneCfg.label)}{doneLabel ? ` · ${doneLabel}` : ''}
              </span>
              {completionByLine && (
                <span className="detail1 text-secondary" style={{ fontSize: 11 }}>{completionByLine}</span>
              )}
              {task.status === 'cancelled' && task.completion_notes && (
                <span className="detail1 text-secondary" style={{ fontSize: 11, maxWidth: 220, textAlign: 'right' }}>
                  {task.completion_notes}
                </span>
              )}
              {/* Names where a machine closure came from. `completion_source: 'system'` alone is a
                  coarse bucket (WF69/Elation, WF37/CMS, WF17 cascades all use it), so the origin
                  comes from the structured stamp, never from parsing completion_notes. */}
              {appt?.state === 'done' && (
                <span className="detail1 text-secondary" style={{ fontSize: 11 }}>{appt.origin}</span>
              )}
            </div>
          ) : appt?.label ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <span className="detail1" style={{ color: appt.tone, fontWeight: 500 }}>{appt.label}</span>
              <span className="detail1 text-secondary" style={{ fontSize: 11 }}>{appt.origin}</span>
            </div>
          ) : (
            dueLabel && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                <span className="detail1" style={{
                  color: isOverdue ? 'var(--wi-status-bad)' : 'var(--wi-text-secondary)',
                  fontWeight: isOverdue && !snz ? 500 : 400,
                }}>
                  {isOverdue ? `Overdue · ${dueLabel}` : `Due ${dueLabel}`}
                </span>
                {/* Cancelled/missed → WF69 restored this due date; done → a reopened auto-completion.
                    Either way the date needs its explanation or it looks like it moved by itself. */}
                {(appt?.state === 'cancelled' || appt?.state === 'done' || appt?.state === 'missed') && (
                  <span className="detail1 text-secondary" style={{ fontSize: 11, maxWidth: 220, textAlign: 'right' }}>
                    {appt.origin}
                  </span>
                )}
              </div>
            )
          )}
          {(canSnooze || canResume || canClose) && (
            <button
              ref={kebabRef}
              onClick={(e) => { e.stopPropagation(); setActionsOpen((o) => !o); }}
              className="task-kebab"
              aria-label="Task actions"
              aria-expanded={actionsOpen}
            >
              •••
            </button>
          )}
          {canNotThisPatient && (
            <button
              onClick={(e) => { e.stopPropagation(); onNotThisPatient(task); }}
              title={callCancelCopy(call).tooltip}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontSize: 11, fontWeight: 500, fontFamily: 'var(--wi-font-family)', color: 'var(--wi-action-primary)',
              }}
            >
              {callCancelCopy(call).action}
            </button>
          )}
          {isDone && onReopen && !frozen && (
            <button
              onClick={handleReopen}
              disabled={reopening}
              title="Reopen this task (undo)"
              style={{
                background: 'none', border: 'none', padding: 0,
                cursor: reopening ? 'default' : 'pointer',
                fontSize: 11, fontWeight: 500, fontFamily: 'var(--wi-font-family)',
                color: 'var(--wi-action-primary)', opacity: reopening ? 0.6 : 1,
              }}
            >
              {reopening ? 'Reopening…' : 'Reopen'}
            </button>
          )}
        </div>
      </div>
      <CareUpdateStatus task={task} ehrPatientId={ehrPatientId} />
      {/* Lab results landed in the Elation chart (WF72) — read-only */}
      <LabResultsStatus task={task} ehrPatientId={ehrPatientId} />
      <SpruceInboundStatus task={task} />
      <ClaimEvidenceStatus task={task} />
      {hasActions && (
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 8,
          marginTop: 12,
        }}>
          {canOrderLab && (
            <button
              onClick={(e) => { e.stopPropagation(); onOrderLab(task); }}
              className="task-action-btn"
              title="Submit this lab order to Elation EHR"
            >
              Order in Elation
            </button>
          )}
          {isAlignmentTask && onAlignCms && (
            <button
              onClick={(e) => { e.stopPropagation(); onAlignCms(task); }}
              className="task-action-btn"
              title="Submit CMS alignment for this patient"
            >
              Align for CMS
            </button>
          )}
          {isEligibilityTask && onRunCmsEligibility && (
            <button
              onClick={(e) => { e.stopPropagation(); onRunCmsEligibility(task); }}
              className="task-action-btn"
              title="Run CMS Medicare eligibility check via WF12"
            >
              Run Eligibility
            </button>
          )}
          {isReportingTask && onRunCmsReporting && (
            <button
              onClick={(e) => { e.stopPropagation(); onRunCmsReporting(task, reportingKind); }}
              className="task-action-btn"
              title="Submit this report to CMS via WF37"
            >
              Run CMS Reporting
            </button>
          )}
          {isCarePlanTask && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenCarePlan(); }}
              className="task-action-btn"
              title="Open the Care Plan tab"
            >
              {carePlanLabel}
            </button>
          )}
          {isElationLinkTask && (
            <a
              href={`${ELATION_PATIENT_BASE_URL}/${ehrPatientId}/`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="task-action-btn"
              title={elationLinkTitle}
            >
              Open in Elation
              <span aria-hidden="true" style={{ fontSize: 11 }}>↗</span>
            </a>
          )}
        </div>
      )}
      </div>
      <div className="task-slide__actions" aria-hidden={!actionsOpen}>
        {canResume && (
          <button
            className="task-slide__action task-slide__action--snooze"
            tabIndex={actionsOpen ? 0 : -1}
            title={snz.reason ? `Snoozed: ${snz.reason}` : 'Snoozed'}
            onClick={(e) => { e.stopPropagation(); setActionsOpen(false); onResume(task); }}
          >
            {`Bring back (${snz.until})`}
          </button>
        )}
        {canSnooze && !snz && (
          <button
            className="task-slide__action task-slide__action--snooze"
            tabIndex={actionsOpen ? 0 : -1}
            title="Pause the reminders for this task for a while"
            onClick={(e) => { e.stopPropagation(); setActionsOpen(false); onSnooze(task); }}
          >
            Snooze
          </button>
        )}
        {canClose && (
          <button
            className="task-slide__action task-slide__action--cancel"
            tabIndex={actionsOpen ? 0 : -1}
            title="Cancel this task — no longer needed (won't be marked completed)"
            onClick={(e) => { e.stopPropagation(); setActionsOpen(false); onCloseTask(task); }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
